import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("checking"); // checking | valid | invalid | photo
  const [firstName, setFirstName] = useState("");
  const [invalidMsg, setInvalidMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setInvalidMsg("Lien invalide."); return; }
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setFirstName(data.first_name || ""); setState("valid"); })
      .catch((err) => { setState("invalid"); setInvalidMsg(formatApiError(err.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Ton mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post(`/auth/invite/${token}`, { password });
      await refresh();
      setState("photo");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/client/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "La photo n'a pas pu être envoyée.");
      setPhotoPreview("");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-cream px-6 py-12 sm:px-8">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }} className="w-full max-w-[420px]">
        <div className="flex justify-center">
          <span data-testid="elevate-logo" className="logo-elevate text-6xl sm:text-7xl">elevate</span>
        </div>

        {state === "checking" && (
          <p data-testid="invite-checking" className="mt-12 text-center font-light text-ink-soft">Vérification de ton lien…</p>
        )}

        {state === "invalid" && (
          <div className="mt-12 text-center">
            <h1 className="font-sans text-2xl font-medium text-ink">Lien indisponible</h1>
            <p data-testid="invite-invalid" className="mt-3 rounded-lg bg-bordeaux-tint px-4 py-3 text-sm font-light text-bordeaux">
              {invalidMsg || "Ce lien n'est plus valide."}
            </p>
            <button data-testid="go-login" onClick={() => navigate("/login")}
              className="mt-6 rounded-full bg-bordeaux px-6 py-3 text-sm font-medium text-white transition hover:bg-bordeaux-dark">
              Aller à la connexion
            </button>
          </div>
        )}

        {state === "valid" && (
          <>
            <div className="mt-12 text-center">
              <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">
                Bienvenue{firstName ? ` ${firstName}` : ""} 🤍
              </h1>
              <p className="mt-3 font-sans text-sm font-light text-ink-soft sm:text-base">
                Choisis ton mot de passe pour activer ton espace membre.
              </p>
            </div>
            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label className="font-sans text-sm font-medium text-ink">Mot de passe</label>
                <input data-testid="invite-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
                  placeholder="Minimum 8 caractères" />
              </div>
              <div>
                <label className="font-sans text-sm font-medium text-ink">Confirme ton mot de passe</label>
                <input data-testid="invite-confirm-input" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
                  placeholder="••••••••" />
              </div>
              {error && (
                <p data-testid="invite-error" className="rounded-lg bg-bordeaux-tint px-4 py-3 font-sans text-sm font-light text-bordeaux">{error}</p>
              )}
              <button data-testid="invite-submit-button" type="submit" disabled={loading}
                className="w-full rounded-full bg-bordeaux py-4 font-sans text-base font-medium text-white transition hover:bg-bordeaux-dark disabled:opacity-60">
                {loading ? "Activation…" : "Activer mon espace"}
              </button>
            </form>
          </>
        )}

        {state === "photo" && (
          <div className="mt-12 text-center">
            <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">Ajoute ta photo 📸</h1>
            <p className="mt-3 font-sans text-sm font-light text-ink-soft sm:text-base">
              Une petite photo de toi pour personnaliser ton espace. Fanny la verra aussi de son côté.
            </p>

            <div className="mt-8 flex justify-center">
              <label data-testid="photo-dropzone" className="group relative flex h-32 w-32 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-bordeaux/40 bg-white transition hover:border-bordeaux">
                {photoPreview ? (
                  <img src={photoPreview} alt="Aperçu" className="h-full w-full object-cover" />
                ) : (
                  <span className="px-3 text-center text-xs font-light text-ink-muted">Clique pour choisir une photo</span>
                )}
                <input data-testid="photo-input" type="file" accept="image/*" onChange={onPhoto} className="absolute inset-0 cursor-pointer opacity-0" />
              </label>
            </div>

            {uploading && <p className="mt-4 text-sm font-light text-ink-soft">Envoi de ta photo…</p>}
            {error && (
              <p data-testid="photo-error" className="mt-4 rounded-lg bg-bordeaux-tint px-4 py-3 font-sans text-sm font-light text-bordeaux">{error}</p>
            )}

            <button data-testid="photo-continue" onClick={() => navigate("/espace")} disabled={uploading || !photoPreview}
              className="mt-8 w-full rounded-full bg-bordeaux py-4 font-sans text-base font-medium text-white transition hover:bg-bordeaux-dark disabled:opacity-60">
              {photoPreview ? "Continuer vers mon espace" : "Ajoute une photo pour continuer"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
